import React, { PropsWithChildren } from "react";
import styled from "styled-components";
import { $stylesReset, IStyles } from "components/common/$styles";
import { Fade, FadeProps } from "react-awesome-reveal";
import { ArrowTriangle } from "icons";
import Img from "next/image";

interface IFaq {
  active?: boolean;
  answer: any;
  question: string;
  animation: string;
}

export interface IFrequentlyAskedQuestionsComponent {
  id?: string;
  initialItems: IFaq[];
}

export const FrequentlyAskedQuestionsComponent: React.FC<
  PropsWithChildren<IFrequentlyAskedQuestionsComponent>
> = ({ id, initialItems, children }): JSX.Element => {
  const [items, setItems] = React.useState<IFaq[]>(initialItems);

  const onClick = React.useMemo(() => {
    return (idx: number) => (event: React.MouseEvent) => {
      event.stopPropagation();
      setItems((_items: IFaq[]): IFaq[] => {
        return _items.map((item, index) => {
          if (index === idx) {
            return { ...item, active: !item?.active };
          }
          return { ...item, active: false };
        });
      });
    };
  }, [setItems]);

  return (
    <Box id={id}>
      <div className="container">
        <Content>
          {children ?? (
            <TextTitle>
              <Img
                src="/images/FAQComponent/FAQ.svg"
                alt="?"
                width={72}
                height={72}
                style={{ gridArea: "icon" }}
              />
              <span style={{ gridArea: "title" }}>Frequently asked</span>
              <span style={{ gridArea: "subtitle" }} data-blue>
                questions
              </span>
            </TextTitle>
          )}
          <Cards>
            {items.map((item, index) => {
              return (
                <Fade
                  direction={item.animation as FadeProps["direction"]}
                  style={{ width: "100%" }}
                  key={index}
                  triggerOnce
                >
                  <Card active={item.active || false} onClick={onClick(index)}>
                    <CardQuestion data-active={item.active}>
                      <span data-grow>{item.question}</span>
                      <ToggleBtn onClick={onClick(index)} active={item.active || false}>
                        <ArrowTriangle />
                      </ToggleBtn>
                    </CardQuestion>
                    {item.active ? <CardAnswer>{item.answer}</CardAnswer> : null}
                  </Card>
                </Fade>
              );
            })}
          </Cards>
        </Content>
      </div>
    </Box>
  );
};

const Box = styled.div`
  ${$stylesReset}
  width: 100%;
`;

const Content = styled.div`
  ${$stylesReset}
  position: relative;
  display: flex;
  flex-flow: column;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  margin-bottom: 48px;
`;

const Text = styled.div`
  ${$stylesReset}
  color: rgba(210, 234, 255, 0.75);
  font-family: var(--font-family-golos);
  font-size: 16px;
  font-weight: 500;
  letter-spacing: 0.02em;
  text-align: center;
`;

const TextTitle = styled(Text)`
  display: grid;
  color: var(--daylight-color);
  font-family: var(--font-family-exo);
  font-size: 24px;
  font-style: italic;
  font-weight: 900;
  line-height: 120%;
  text-transform: uppercase;
  gap: 12px 24px;
  margin-bottom: 64px;
  align-items: center;
  text-align: left;
  grid-template-areas:
    "icon title"
    "icon subtitle";
  grid-template-columns: auto 1fr;
  & [data-blue] {
    color: var(--pure-sky);
    font-size: 48px;
    @media (max-width: 500px) {
      font-size: 26px;
    }
  }

  @media (max-width: 500px) {
    font-size: 20px;
    gap: 0 12px;
    margin-bottom: 32px;
  }
`;

const Cards = styled.div`
  display: flex;
  flex-flow: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

const Card = styled.div<{ active: boolean }>`
  box-sizing: border-box;
  position: relative;
  display: flex;
  flex-flow: column;
  justify-content: center;
  gap: 16px;
  width: 100%;
  min-height: 48px;
  padding: ${({ active }) => (active ? "24px" : "18px 24px")};
  border: 1px solid ${({ active }) => (active ? "var(--pure-sky)" : "transparent")};
  background: ${({ active }) => (active ? "#1B2035" : "var(--global-bg-dark-1)")};
  border-radius: 12px;
  transition: all 0.3s;
  cursor: pointer;
  &:hover {
    border-color: var(--pure-sky);
  }
`;

const CardAnswer = styled.div<IStyles>`
  width: 100%;
  overflow: hidden;

  color: rgba(200, 215, 245, 0.75);
  font-family: var(--font-family-golos);
  font-size: 14px;
  font-weight: 400;

  text-align: left;
  white-space: pre-wrap;
  word-wrap: break-word;

  ${({ styles }) => styles}
`;

const CardQuestion = styled.div<IStyles>`
  display: flex;
  justify-content: flex-start;
  gap: 12px;

  color: var(--daylight-color);
  font-family: var(--font-family-exo);
  font-size: 15px;
  font-style: italic;
  font-weight: 800;
  text-transform: uppercase;

  & [data-grow] {
    flex-grow: 1;
    text-align: left;
  }

  &[data-active="true"] {
    color: #d2eaff;
  }

  ${({ styles }) => styles}
`;

const ToggleBtn = styled.div<{ active: boolean }>`
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 24px;
  width: 24px;
  border-radius: 6px;
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.3s;
  background-color: ${({ active }) => (active ? "var(--pure-sky)" : "var(--dark-space)")};

  & svg {
    transition: transform 0.3s;
    transform: rotate(${({ active }) => (active ? "-90deg" : "90deg")});
  }

  & path {
    transition: fill 0.3s;
    fill: ${({ active }) => (active ? "#1B2035" : "var(--daylight-75-color)")};
  }

  &:hover {
    border-color: var(--pure-sky);
  }
`;
